/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createReducer } from '@reduxjs/toolkit';

import { startDebug, startFree, startTournament, startTraining } from '~/game';
import { EventLogEntry } from '~/server/src/dtos';

import type { HistoryCursor } from './actions';
import {
  setCardDice,
  setChatLastReadIncomingEventIndex,
  setChatPanelState,
  setDebugPanelState,
  setEventLog,
  setHandiFailureHorseIndex,
  setHistoryCursor,
  setHistoryPanelState,
  setHorseBonusCards,
  setHorseCards,
  setHorsesBonusCards,
  setHorsesCards,
  setLastCard,
  setLastCardHorseIndex,
  setLastTurnDicePlayerId,
  setLastTurnDices,
  setLastTurnDicesCur,
  setLatestGameStateAnchor,
  setOwnHorseRemovedCards,
  setSelectedCardIndexLocal,
  setSelectedDicesLocal,
  setSelectedStep,
  setTanstackDevtoolsOpen,
  setUserId,
} from './actions';

export interface OverlayState {
  readonly handiFailureHorseIndex: number;
  readonly cardDice: number;
  readonly lastCardHorseIndex: number;
  readonly lastCard: number;
  readonly lastTurnDicePlayerId: number;
  readonly lastTurnDices: number[];
  readonly lastTurnDicesCur: number;

  readonly horsesCards: number[][][];
  readonly horsesBonusCards: number[];
  readonly ownHorseRemovedCards: number[];

  readonly selectedDicesLocal: number;
  readonly selectedCardIndexLocal: number;
  readonly eventLog: EventLogEntry[];
  /**
   * Index into `eventLog` of the last incoming (`NEWMESSAGE` sent by someone else) chat message
   * that has been marked as read by the local user.
   */
  readonly chatLastReadIncomingEventIndex: number;
  readonly userId: string;
  readonly latestGameStateAnchor: Record<string, unknown> | null;
  /** @deprecated Prefer historyCursor; kept for compatibility during migration. */
  readonly selectedStep: number | null;
  readonly historyCursor: HistoryCursor;
  readonly panelState: {
    history: { isVisible: boolean; isExpanded: boolean };
    chat: { isVisible: boolean; isExpanded: boolean };
    debug: { isVisible: boolean };
    tanstackDevtoolsOpen: boolean;
  };
}

export const initialOverlayState: OverlayState = {
  handiFailureHorseIndex: -1,
  cardDice: -1,
  lastCardHorseIndex: -1,
  lastCard: -1,
  lastTurnDicePlayerId: -1,
  lastTurnDices: [],
  lastTurnDicesCur: -1,
  horsesCards: [],
  horsesBonusCards: [],
  ownHorseRemovedCards: [],
  selectedDicesLocal: -1,
  selectedCardIndexLocal: -1,
  eventLog: [],
  chatLastReadIncomingEventIndex: -1,
  userId: '',
  latestGameStateAnchor: null,
  selectedStep: null,
  historyCursor: { kind: 'live' },
  panelState: {
    history: { isVisible: false, isExpanded: false },
    chat: { isVisible: false, isExpanded: false },
    debug: { isVisible: false },
    tanstackDevtoolsOpen: false,
  },
};

export default createReducer(initialOverlayState, (builder) => {
  builder
    .addCase(startTraining, () => initialOverlayState)
    .addCase(startFree, () => initialOverlayState)
    .addCase(startDebug, () => initialOverlayState)
    .addCase(startTournament, () => initialOverlayState)
    .addCase(setHandiFailureHorseIndex, (state, action) => {
      state.handiFailureHorseIndex = action.payload;
    })
    .addCase(setCardDice, (state, action) => {
      state.cardDice = action.payload;
    })
    .addCase(setLastCardHorseIndex, (state, action) => {
      state.lastCardHorseIndex = action.payload;
    })
    .addCase(setLastCard, (state, action) => {
      state.lastCard = action.payload;
    })
    .addCase(setLastTurnDicePlayerId, (state, action) => {
      state.lastTurnDicePlayerId = action.payload;
    })
    .addCase(setLastTurnDices, (state, action) => {
      state.lastTurnDices = action.payload;
    })
    .addCase(setLastTurnDicesCur, (state, action) => {
      state.lastTurnDicesCur = action.payload;
    })
    .addCase(setHorsesCards, (state, action) => {
      state.horsesCards = action.payload;
    })
    .addCase(setHorseCards, (state, action) => {
      state.horsesCards[action.payload.index] = action.payload.value;
    })
    .addCase(setHorsesBonusCards, (state, action) => {
      state.horsesBonusCards = action.payload;
    })
    .addCase(setHorseBonusCards, (state, action) => {
      state.horsesBonusCards[action.payload.index] = action.payload.value;
    })
    .addCase(setOwnHorseRemovedCards, (state, action) => {
      state.ownHorseRemovedCards = action.payload;
    })
    .addCase(setSelectedDicesLocal, (state, action) => {
      state.selectedDicesLocal = action.payload;
    })
    .addCase(setSelectedCardIndexLocal, (state, action) => {
      state.selectedCardIndexLocal = action.payload;
    })
    .addCase(setEventLog, (state, action) => {
      state.eventLog = action.payload;
    })
    .addCase(setChatLastReadIncomingEventIndex, (state, action) => {
      state.chatLastReadIncomingEventIndex = action.payload;
    })
    .addCase(setUserId, (state, action) => {
      state.userId = action.payload;
    })
    .addCase(setLatestGameStateAnchor, (state, action) => {
      state.latestGameStateAnchor = action.payload;
    })
    .addCase(setSelectedStep, (state, action) => {
      state.selectedStep = action.payload;
      state.historyCursor =
        action.payload === null
          ? { kind: 'live' }
          : { kind: 'semantic', originalIndex: action.payload };
    })
    .addCase(setHistoryCursor, (state, action) => {
      state.historyCursor = action.payload;
      state.selectedStep = action.payload.kind === 'live' ? null : action.payload.originalIndex;
    })
    .addCase(setHistoryPanelState, (state, action) => {
      state.panelState.history = action.payload;
    })
    .addCase(setChatPanelState, (state, action) => {
      state.panelState.chat = action.payload;
    })
    .addCase(setDebugPanelState, (state, action) => {
      state.panelState.debug = action.payload;
    })
    .addCase(setTanstackDevtoolsOpen, (state, action) => {
      state.panelState.tanstackDevtoolsOpen = action.payload;
    });
});
